import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { getRequestStatusColor } from '../placeholders/planningDomain';
import { formatRequestDateEs, formatRequestQty } from '../placeholders/commercialRequestsUtils';

const TABLE_COLUMNS = [
  { key: 'Solicitud', align: 'left', sortKey: 'request_code' },
  { key: 'Producto', align: 'left', sortKey: 'product_name' },
  { key: 'Color', align: 'left', sortKey: 'color_name' },
  { key: 'Cantidad', align: 'right', sortKey: 'quantity' },
  { key: 'Fecha límite', align: 'left', sortKey: 'needed_by_date' },
  { key: 'Estado', align: 'center', sortKey: 'request_status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'request_code':
      return String(row.request_code || '').toLowerCase();
    case 'product_name':
      return String(row.product_name || row.product_code || '').toLowerCase();
    case 'color_name':
      return `${row.color_code || ''} ${row.color_name || ''}`.toLowerCase();
    case 'quantity':
      return Number(row.quantity || 0);
    case 'needed_by_date':
      return row.needed_by_date ? new Date(row.needed_by_date).getTime() : 0;
    case 'request_status':
      return String(row.request_status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const TcoatProductRequestItemsTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  hideChrome = false,
  onRequestClick,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('needed_by_date');
  const [sortDir, setSortDir] = useState('desc');

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.request_status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.request_code,
          row.reference,
          row.product_code,
          row.product_name,
          row.color_code,
          row.color_name,
          row.quotation_code,
          row.sale_code,
          row.request_status,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) list = list.filter((row) => String(row.request_status || '').trim() === filterStatus);
    return list;
  }, [rows, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const handleSortClick = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(['needed_by_date', 'quantity'].includes(key) ? 'desc' : 'asc');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || filterStatus);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} ítems`
        : `${rows.length} ítems`);

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    minWidth: 140,
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const statusPill = (status) => {
    const color = getRequestStatusColor(status);
    return (
      <span
        style={{
          display: 'inline-flex',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          background: `${color}22`,
          color,
          border: `1px solid ${color}55`,
        }}
      >
        {status || '—'}
      </span>
    );
  };

  return (
    <div style={hideChrome ? { background: bg } : { border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden', background: bg }}>
      {hideChrome ? null : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>Ítems T-COAT</h3>
            <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
          </div>
        </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Solicitud, producto, color, venta…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Estado</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button type="button" onClick={clearFilters} style={{ ...selectStyle, cursor: 'pointer' }}>
            Limpiar
          </button>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando ítems…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay ítems para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr
                  key={row.id}
                  style={{ borderBottom: `1px solid ${border}`, background: bg }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = rowHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = bg;
                  }}
                >
                  <td style={{ padding: `${padV}px ${padH}px` }}>
                    <button
                      type="button"
                      onClick={() => onRequestClick?.({ id: row.request_id, request_code: row.request_code })}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 12, color: ui.link || '#2563eb' }}>
                        {row.request_code || '—'}
                      </div>
                      <div style={{ fontSize: 11, color: muted }}>{row.reference || '—'}</div>
                    </button>
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px` }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: text }}>{row.product_name || '—'}</div>
                    <div style={{ fontSize: 11, color: muted }}>{row.product_code || '—'}</div>
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: text }}>
                    {[row.color_code, row.color_name].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, textAlign: 'right', fontWeight: 600, color: text }}>
                    {formatRequestQty(row.quantity, row.unit)}
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px`, fontSize: 12, color: muted }}>
                    {formatRequestDateEs(row.needed_by_date)}
                  </td>
                  <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                    {statusPill(row.request_status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TcoatProductRequestItemsTable;
